/**
 * GlobalToolbar — stage-agnostic top-left controls (mute, fullscreen).
 *
 * Lives on document.body so it persists across stage swaps (CellStage's UI
 * gets torn down and rebuilt on transition; we don't want users hunting for
 * the mute button after a stage change). Bottom-right corner sits above
 * the version tag — top-left collides with phase 2's biomes/world view.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import { togglePanel as toggleHeritagePanel, hasAffordableHeritage } from './AchievementsPanel';
import { toggleScoreboard } from './ScoreboardPanel';

let installed = false;

export function installGlobalToolbar(): void {
  if (installed) return;
  installed = true;

  const bar = document.createElement('div');
  bar.id = 'global-toolbar';
  bar.setAttribute('style',
    'position:fixed;bottom:32px;right:8px;display:flex;gap:6px;z-index:10000;' +
    'pointer-events:none;');
  document.body.appendChild(bar);

  const btnStyle =
    'width:32px;height:32px;border-radius:8px;' +
    'background:rgba(20,30,40,0.55);border:1px solid rgba(180,220,230,0.25);' +
    'color:rgba(180,220,230,0.85);font:16px/1 Inter,system-ui,sans-serif;' +
    'cursor:pointer;display:flex;align-items:center;justify-content:center;' +
    'padding:0;user-select:none;pointer-events:auto;';

  // ---- Mute ----
  const muteBtn = document.createElement('button');
  muteBtn.id = 'mute-toggle';
  muteBtn.title = 'Mute audio (M)';
  muteBtn.setAttribute('style', btnStyle);
  const renderMute = (): void => {
    const muted = saveManager.getMeta().settings.muted;
    muteBtn.textContent = muted ? '🔇' : '🔊';
    muteBtn.setAttribute('aria-pressed', String(muted));
  };
  renderMute();
  muteBtn.addEventListener('click', () => {
    const muted = !saveManager.getMeta().settings.muted;
    bus.emit('audio:setMuted', { muted });
    renderMute();
  });
  bar.appendChild(muteBtn);

  // ---- Fullscreen ----
  const fsBtn = document.createElement('button');
  fsBtn.id = 'fullscreen-toggle';
  fsBtn.title = 'Toggle fullscreen (F)';
  fsBtn.setAttribute('style', btnStyle);
  const renderFs = (): void => {
    const on = !!document.fullscreenElement;
    // ⛶ for enter, ⛶ collapsed icon for exit — use distinct glyphs so the
    // state is readable even when the button hover state isn't visible.
    fsBtn.textContent = on ? '🗗' : '⛶';
    fsBtn.setAttribute('aria-pressed', String(on));
  };
  renderFs();
  const toggleFs = (): void => {
    const root = document.documentElement;
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      const fn =
        root.requestFullscreen ||
        (root as any).webkitRequestFullscreen ||
        (root as any).msRequestFullscreen;
      if (!fn) return;
      try { (fn.call(root) as Promise<void> | void)?.catch?.(() => {}); }
      catch { /* ignore */ }
    }
  };
  fsBtn.addEventListener('click', toggleFs);
  document.addEventListener('fullscreenchange', renderFs);
  window.addEventListener('keydown', (e) => {
    if (e.key === 'f' || e.key === 'F') {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      toggleFs();
    }
  });
  bar.appendChild(fsBtn);

  // ---- Scoreboard ----
  const scoreBtn = document.createElement('button');
  scoreBtn.id = 'scoreboard-toggle';
  scoreBtn.title = 'Scoreboard';
  scoreBtn.setAttribute('style', btnStyle);
  scoreBtn.textContent = '🏆';
  scoreBtn.addEventListener('click', () => toggleScoreboard());
  bar.appendChild(scoreBtn);

  // ---- Heritage / Achievements ----
  // Wrapper holds the button + an absolute "!" badge. The button itself
  // stays a flexbox so its emoji centers regardless of badge presence.
  const heritageWrap = document.createElement('div');
  heritageWrap.style.cssText = 'position:relative;pointer-events:none;';
  const heritageBtn = document.createElement('button');
  heritageBtn.id = 'heritage-toggle';
  heritageBtn.title = 'Heritage & achievements (H)';
  heritageBtn.setAttribute('style', btnStyle);
  heritageBtn.textContent = '🧬';
  heritageBtn.addEventListener('click', () => toggleHeritagePanel());

  const heritageBadge = document.createElement('div');
  heritageBadge.setAttribute('style',
    'position:absolute;top:-4px;right:-4px;min-width:14px;height:14px;' +
    'padding:0 3px;border-radius:7px;background:#fadf7c;color:#1a1408;' +
    'font:700 10px/14px Inter,system-ui,sans-serif;text-align:center;' +
    'box-shadow:0 0 0 2px rgba(8,12,18,0.95),0 0 8px rgba(250,223,124,0.7);' +
    'pointer-events:none;display:none;');
  heritageBadge.textContent = '!';

  heritageWrap.appendChild(heritageBtn);
  heritageWrap.appendChild(heritageBadge);

  const refreshBadge = (): void => {
    heritageBadge.style.display = hasAffordableHeritage() ? 'block' : 'none';
  };
  bus.on('meta:shardsChanged', refreshBadge);
  // New Game / Import / hard reset all push save:loaded — re-check the
  // badge so a fresh save's shard balance reflects accurately.
  bus.on('save:loaded', refreshBadge);
  // Initial sample after install — re-check on the next frame so any
  // late panel-install (which carries the data getter) is in place.
  requestAnimationFrame(refreshBadge);

  window.addEventListener('keydown', (e) => {
    if (e.key === 'h' || e.key === 'H') {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      toggleHeritagePanel();
    }
  });
  bar.appendChild(heritageWrap);

  // Subtle pulse on the button when an achievement is earned, so the
  // player notices unspent shards even if they ignored the toast.
  bus.on('achievement:earned', () => {
    heritageBtn.animate(
      [
        { boxShadow: '0 0 0 0 rgba(250,223,124,0.0)' },
        { boxShadow: '0 0 0 6px rgba(250,223,124,0.4)' },
        { boxShadow: '0 0 0 0 rgba(250,223,124,0.0)' },
      ],
      { duration: 1200, iterations: 2 },
    );
  });
}
